import { useMutation, useQuery } from "@apollo/client";
import { Alert, Box, FormControlLabel, Stack, Switch, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import {
  type CreateLinearIssueResult,
  CreateLinearIssueDialog as BaseCreateLinearIssueDialog,
} from "components/CreateLinearIssueDialog";
import {
  ERROR_CLUSTER_RUN_ID_KEY,
  ERROR_CLUSTER_GROUP_ID_KEY,
} from "encamp-omnisearch/src/constants/keyNames";
import { gql } from "generated-graphql";
import pluralize from "pluralize";
import { useCallback, useMemo, useState } from "react";
import { UPDATE_WORKFLOW_TAGS_BULK_MUTATION } from "routes/Staff/Fulfillment/WorkflowActivitiesTable/ManageWorkflowTags";

const ERROR_CLUSTER_FOR_WORKFLOW_QUERY = gql(`
  query ErrorClusterForWorkflow($workflowId: ID!) {
    errorClusterForWorkflow(workflowId: $workflowId) {
      runId
      groupId
      groupSize
      errorPattern
      states
      workflowsWithTags {
        workflowId
        tags
      }
    }
  }
`);

interface FacilityInfo {
  id: string;
  name: string;
  city: string;
  state: string;
  streetAddress1?: string | null;
}

interface TenantInfo {
  id: string;
  name: string;
}

interface ActivityInfo {
  id: string;
  type: string;
  description?: string | null;
}

interface CreateLinearIssueDialogProps {
  open: boolean;
  onClose: () => void;
  workflowId: string;
  reportId: string;
  facility: FacilityInfo;
  tenant: TenantInfo;
  activity: ActivityInfo;
  errorMessage: string;
}

export function CreateLinearIssueDialog({
  open,
  onClose,
  workflowId,
  reportId,
  facility,
  tenant,
  activity,
  errorMessage,
}: CreateLinearIssueDialogProps) {
  const alerts = useAlerts();
  const [createForCluster, setCreateForCluster] = useState(false);

  // Query for error cluster information
  const { data: clusterData, loading: clusterLoading } = useQuery(
    ERROR_CLUSTER_FOR_WORKFLOW_QUERY,
    {
      variables: { workflowId },
      skip: !open || !workflowId,
    }
  );

  const errorCluster = clusterData?.errorClusterForWorkflow;
  const isPartOfCluster = errorCluster && errorCluster.groupSize > 1;

  const [updateWorkflowTags] = useMutation(UPDATE_WORKFLOW_TAGS_BULK_MUTATION);

  // Build the fulfillment details URL
  const fulfillmentDetailsUrl = useMemo(() => {
    return `${window.location.origin}/staff/fulfillment/r/${reportId}/activities/${activity.id}`;
  }, [reportId, activity.id]);

  // Build the workflows list URL for cluster view
  const viewWorkflowsUrl = useMemo(() => {
    if (!errorCluster) return null;
    const omnisearch = encodeURIComponent(
      `${ERROR_CLUSTER_RUN_ID_KEY}:${errorCluster.runId} ${ERROR_CLUSTER_GROUP_ID_KEY}:${errorCluster.groupId}`
    );
    return `${window.location.origin}/staff/fulfillment/workflows?omnisearch=${omnisearch}`;
  }, [errorCluster]);

  const defaultTitle = useMemo(() => {
    if (createForCluster && errorCluster) {
      const sortedStates = [...errorCluster.states].sort();
      const statesDisplay = sortedStates.length <= 3 ? sortedStates.join(", ") : "Multiple States";
      const truncatedPattern =
        errorCluster.errorPattern.length > 25
          ? `${errorCluster.errorPattern.slice(0, 25)}...`
          : errorCluster.errorPattern;
      return `[${errorCluster.groupSize}: ${statesDisplay}] Fulfillment Error: ${truncatedPattern}`;
    } else {
      const truncatedError =
        errorMessage.length > 50 ? `${errorMessage.slice(0, 50)}...` : errorMessage;
      return `[${facility.state}] Fulfillment Error: ${facility.name} - ${truncatedError}`;
    }
  }, [createForCluster, errorCluster, errorMessage, facility]);

  const buildDescription = useCallback(() => {
    if (createForCluster && errorCluster) {
      const sortedStates = [...errorCluster.states].sort();
      return [
        `**Error Pattern**: ${errorCluster.errorPattern}`,
        "",
        `**View Workflows**: ${viewWorkflowsUrl}`,
        "",
        `**Example Fulfillment Detail**: ${fulfillmentDetailsUrl}`,
        "",
        `**Group Size**: ${errorCluster.groupSize}`,
        "",
        `**States**: ${sortedStates.join(", ")}`,
        "",
        `**Example Facility**: ${facility.name}`,
        `**Customer**: ${tenant.name}`,
      ].join("\n");
    } else {
      return [
        `**Error Message**: ${errorMessage}`,
        "",
        `**Fulfillment Details**: ${fulfillmentDetailsUrl}`,
        "",
        `**Facility**: ${facility.name}`,
        `**Address**: ${facility.streetAddress1 || ""}, ${facility.city}, ${facility.state}`,
        `**Customer**: ${tenant.name}`,
        "",
        `**Workflow ID**: ${workflowId}`,
        `**Activity**: ${activity.description || activity.type}`,
      ].join("\n");
    }
  }, [
    createForCluster,
    errorCluster,
    errorMessage,
    facility,
    tenant,
    workflowId,
    activity,
    fulfillmentDetailsUrl,
    viewWorkflowsUrl,
  ]);

  const handleCreated = useCallback(
    async ({ issueIdentifier }: CreateLinearIssueResult) => {
      let workflowUpdates: Array<{
        workflowId: string;
        tagNames: string[];
      }> = [];

      if (createForCluster && errorCluster) {
        workflowUpdates = errorCluster.workflowsWithTags.map((workflow) => ({
          workflowId: workflow.workflowId,
          tagNames: [...new Set([...workflow.tags, issueIdentifier, "engineering"])],
        }));
      } else {
        const currentWorkflowTags =
          errorCluster?.workflowsWithTags.find((w) => w.workflowId === workflowId)?.tags ?? [];
        workflowUpdates = [
          {
            workflowId,
            tagNames: [...new Set([...currentWorkflowTags, issueIdentifier, "engineering"])],
          },
        ];
      }

      if (workflowUpdates.length > 0) {
        await updateWorkflowTags({
          variables: {
            workflowUpdates,
          },
        });

        alerts.success(
          `Tagged ${workflowUpdates.length} ${pluralize(
            "workflow",
            workflowUpdates.length
          )} with ${issueIdentifier}`
        );
      }
    },
    [createForCluster, errorCluster, workflowId, updateWorkflowTags, alerts]
  );

  const handleClose = useCallback(() => {
    setCreateForCluster(false);
    onClose();
  }, [onClose]);

  const alertContent = useMemo(() => {
    if (clusterLoading || !isPartOfCluster || !errorCluster) return undefined;
    return (
      <Alert severity="info" sx={{ alignItems: "flex-start" }}>
        <Stack spacing={1}>
          <Typography variant="body2">
            This error is part of an Error Cluster group with{" "}
            <strong>{errorCluster.groupSize}</strong> {pluralize("report", errorCluster.groupSize)}.
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={createForCluster}
                onChange={(e) => setCreateForCluster(e.target.checked)}
                size="small"
              />
            }
            label={
              <Typography variant="body2">
                Create ticket for entire cluster group ({errorCluster.groupSize}{" "}
                {pluralize("report", errorCluster.groupSize)})
              </Typography>
            }
          />
        </Stack>
      </Alert>
    );
  }, [clusterLoading, isPartOfCluster, errorCluster, createForCluster]);

  const previewContent = useMemo(
    () => (
      <>
        <Typography variant="subtitle2" gutterBottom>
          {createForCluster ? "Error Pattern" : "Error Message"}
        </Typography>
        <Typography variant="body2" sx={{ mb: 2, wordBreak: "break-word" }}>
          {createForCluster && errorCluster ? errorCluster.errorPattern : errorMessage}
        </Typography>

        <Typography variant="subtitle2" gutterBottom>
          Facility
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {facility.name} ({facility.city}, {facility.state})
        </Typography>

        <Typography variant="subtitle2" gutterBottom>
          Customer
        </Typography>
        <Typography variant="body2">{tenant.name}</Typography>

        {createForCluster && errorCluster && (
          <>
            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
              Group Details
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              <Typography component="li" variant="body2">
                Group Size: {errorCluster.groupSize}
              </Typography>
              <Typography component="li" variant="body2">
                States: {[...errorCluster.states].sort().join(", ")}
              </Typography>
            </Box>
          </>
        )}
      </>
    ),
    [createForCluster, errorCluster, errorMessage, facility, tenant]
  );

  const footerContent = useMemo(
    () => (
      <Typography variant="body2" color="text.secondary">
        {createForCluster && errorCluster ? (
          <>
            After creation, all {errorCluster.workflowsWithTags.length}{" "}
            {pluralize("workflow", errorCluster.workflowsWithTags.length)} in this group will be
            tagged with the ticket ID and "engineering".
          </>
        ) : (
          <>After creation, this workflow will be tagged with the ticket ID and "engineering".</>
        )}
      </Typography>
    ),
    [createForCluster, errorCluster]
  );

  return (
    <BaseCreateLinearIssueDialog
      open={open}
      onClose={handleClose}
      defaultTitle={defaultTitle}
      buildDescription={buildDescription}
      onCreated={handleCreated}
      alertContent={alertContent}
      previewContent={previewContent}
      footerContent={footerContent}
    />
  );
}
